'use client'

import React, { useRef, useState, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import scrollToElement from '../utils/scroll-to-element'
import { useResponsive, ScrollOffset, useScrollOffset, useIsSticky } from '../hooks'
import useUniqueId from '../hooks/use-unique-id'
import { rem } from '@dustin-web/microsite-config'
import { AccordionWrapperProps } from './types'

const ACCORDION_BUTTON_HEIGHT = 4.9
const ACCORDION_BUTTON_MOBILE_HEIGHT = 6.5
const ACCORDION_BUTTON_SLIM_HEIGHT = 4.1
const ACCORDION_BUTTON_STRIPPED_HEIGHT = 4.8
const ACCORDION_BUTTON_STRIPPED_SLIM_HEIGHT = 3.2

export const AccordionWrapper = ({
  button: AccordionButton,
  buttonClick,
  buttonRef,
  children,
  className = null,
  extOpen = false,
  extState = false,
  firstAndLastBorder = false,
  id = '',
  isOpen,
  noPanelPadding = false,
  panelId,
  setButtonClick,
  setExtOpen = () => null,
  setOpen,
  slim = false,
  stickyScrollTo = false,
  stripped = false,
  strippedDark = false,
  sticky = false,
}: AccordionWrapperProps) => {
  const { scrollOffset, localScrollOffset, setOffsetTop } = useScrollOffset()
  const { isSmallScreen } = useResponsive()

  const { isSticky } = useIsSticky<HTMLButtonElement>(buttonRef)

  const getScrollTopRem = useCallback(
    (isSmallScreen: boolean, stripped: boolean, slim: boolean) => {
      if (slim) {
        return stripped ? ACCORDION_BUTTON_STRIPPED_SLIM_HEIGHT : ACCORDION_BUTTON_SLIM_HEIGHT
      }
      if (stripped) {
        return ACCORDION_BUTTON_STRIPPED_HEIGHT
      }
      return isSmallScreen ? ACCORDION_BUTTON_MOBILE_HEIGHT : ACCORDION_BUTTON_HEIGHT
    },
    []
  )

  useEffect(() => {
    setOffsetTop(rem(getScrollTopRem(isSmallScreen, stripped, slim)))
  }, [isSmallScreen, stripped, slim])

  useEffect(() => {
    if (buttonClick) {
      if (!isOpen && stickyScrollTo) {
        if (buttonRef.current && isSticky) {
          scrollToElement(buttonRef.current, scrollOffset.top)
        }
      }
      setButtonClick(false)
    }
  }, [buttonClick, isOpen])

  //TODO Maybe do this in a better way so we don't need to disable eslinst
  // If an external open-state is included, keep it synced
  useEffect(() => {
    if (extState && setExtOpen) {
      if (isOpen !== extOpen) {
        setExtOpen(isOpen)
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (extState) {
      const timeout = setTimeout(() => {
        if (isOpen !== extOpen) {
          setOpen(extOpen)
        }
      }, 0)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [extOpen])

  return (
    <>
      <ScrollOffset.Provider value={localScrollOffset}>
        <div
          id={id}
          className={classNames('c-accordion ', className, {
            'c-accordion--slim': slim,
            'c-accordion--active': isOpen,
            'c-accordion--stripped': stripped,
            'c-accordion--stripped--dark': strippedDark,
            'c-accordion--first-last-border': firstAndLastBorder && !stripped,
            'c-accordion--sticky': sticky,
          })}>
          {AccordionButton}
          <div
            id={panelId}
            className={classNames('c-accordion__panel', {
              'u-py-0 u-px-0': noPanelPadding,
            })}
            aria-hidden={!isOpen}>
            {children}
          </div>
        </div>
      </ScrollOffset.Provider>
    </>
  )
}

export const useAccordionWrapper = ({
  extOpen,
  handleAccordionIsOpen,
}: {
  extOpen?: boolean
  handleAccordionIsOpen?: (isOpen: boolean) => void
}) => {
  const [isOpen, setOpen] = useState(extOpen ?? false)
  const [buttonClick, setButtonClick] = useState(false)
  const panelId = useUniqueId('accordion')
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (extOpen !== undefined) {
      setOpen(extOpen)
    }
  }, [extOpen])

  useEffect(() => {
    if (handleAccordionIsOpen) {
      handleAccordionIsOpen(isOpen)
    }
  }, [isOpen, handleAccordionIsOpen])
  return {
    isOpen,
    setOpen,
    buttonClick,
    setButtonClick,
    panelId,
    buttonRef,
  }
}
